import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Alert,
  Skeleton,
  Stack,
} from "@mui/material";
import { Add as AddIcon, Refresh as RefreshIcon, Business as BusinessIcon } from "@mui/icons-material";
import { useTranslation } from "i18n-fe";
import { useNavigate } from "react-router-dom";
import { useOrganizations } from "../hooks/useOrganizations";
import { PageLayout, StyledBreadcrumb, NewItemCard, ItemCard } from "common-fe";

export default function Organizations() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const {
    organizations,
    loading,
    error,
    refetch
  } = useOrganizations();

  const [successMessage, setSuccessMessage] = useState("");

  // Handle creating a new organization
  const handleCreateNew = () => {
    navigate("/orgs/new");
  };

  // Handle editing an organization
  const handleEdit = (organization) => {
    // Only allow editing if user is the owner
    if (!organization.owner) {
      return; // Silently ignore edit attempts for non-owned organizations
    }
    navigate(`/orgs/${organization.id}`);
  };

  // Handle managing members
  const handleMembers = (organization) => {
    // Only allow managing members if user is the owner
    if (!organization.owner) {
      return; // Silently ignore member management attempts for non-owned organizations
    }
    navigate(`/orgs/${organization.id}/members`);
  };

  // Handle retry
  const handleRetry = () => {
    refetch();
  };

  // Clear success message on mount if it exists
  React.useEffect(() => {
    // Check if we came back from a successful create/edit
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('success') === 'created') {
      setSuccessMessage(t("organizations.createForm.createSuccess"));
      setTimeout(() => setSuccessMessage(""), 5000);
      // Clean up the URL
      window.history.replaceState({}, '', '/orgs');
    } else if (searchParams.get('success') === 'updated') {
      setSuccessMessage(t("organizations.createForm.updateSuccess"));
      setTimeout(() => setSuccessMessage(""), 5000);
      // Clean up the URL
      window.history.replaceState({}, '', '/orgs');
    }
  }, [t]);

  const breadcrumbs = [
    <StyledBreadcrumb key="orgs" label={t("organizations.organizationsPageTitle")} />
  ];

  // Render loading state
  if (loading) {
    return (
      <PageLayout breadcrumbs={breadcrumbs}>
        <Stack spacing={3}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={180} sx={{ borderRadius: 1 }} />
          ))}
        </Stack>
      </PageLayout>
    );
  }

  // Render error state
  if (error) {
    return (
      <PageLayout breadcrumbs={breadcrumbs}>
        <Alert severity="error" sx={{ mt: 2 }}>
          {t("organizations.apiConnectionError")}
        </Alert>
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshIcon />}
          sx={{ mt: 2 }}
          onClick={handleRetry}
          data-testid="retry-button"
        >
          {t("organizations.retryButton")}
        </Button>
      </PageLayout>
    );
  }

  return (
    <PageLayout breadcrumbs={breadcrumbs} maxWidth={600}>
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      <Stack spacing={3}>
       
        {organizations.map((org) => {
          const tags = [
            {
              label: org.active
                ? t("organizations.organizationCard.activeStatus")
                : t("organizations.organizationCard.inactiveStatus"),
              color: org.active ? "success" : "default",
            },
            ...(org.owner
              ? [{ label: t("organizations.organizationCard.owner") }]
              : []),
          ];

          return (
            <ItemCard
              key={org.id}
              title={org.name}
              icon={<BusinessIcon />}
              tags={tags}
              showEdit={org.owner}
              onEdit={() => handleEdit(org)}
              showMembers={org.owner}
              onMembers={() => handleMembers(org)}
              data-testid={`organization-card-${org.id}`}
              editButtonTestId="edit-organization-button"
              membersButtonTestId="members-organization-button"
            />
          );
        })}
         <NewItemCard 
          onClick={handleCreateNew} 
          label={t("organizations.newOrganizationButton")}
          data-testid="new-organization-card-button"
        />
      </Stack>
    </PageLayout>
  );
}
